import { Outlet, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AuditorLayout } from '../auditor/AuditorLayout';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { useEffect, useState } from 'react';
import { setAuthTokenProvider, setCompanyIdProvider } from '../../services/api/ApiService';
import { setCurrentUserForActivities } from '../../hooks/useActivityTracking';
import { useAuthStore } from '../../stores/authStore';
import { Loader2 } from 'lucide-react';
import { isAuditRole } from '../../utils/roles';

/**
 * Route protégée spécifique aux utilisateurs auditeurs (role === 'audit').
 *
 * Différences avec ProtectedRoute :
 * - N'exige PAS d'organization (les auditeurs n'en ont pas)
 * - Utilise AuditorLayout au lieu de Layout
 * - Redirige les non-auditeurs vers '/'
 * - Redirige vers /auditor/clients si aucun activeCompany n'est sélectionné
 *   (sauf si on est déjà sur la page clients)
 */
export function AuditorProtectedRoute() {
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect, getAccessTokenSilently, user } = useAuth0();
  const { initialize, loading: authStoreLoading, user: authStoreUser } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);

  // Configure token provider
  useEffect(() => {
    setAuthTokenProvider(async () => {
      try {
        if (isAuthenticated) {
          return await getAccessTokenSilently();
        }
        return null;
      } catch (error) {
        console.error('Error getting access token', error);
        return null;
      }
    });

    // Provider pour X-Company-Id (auditeurs avec entreprise active)
    setCompanyIdProvider(() => {
      const { user: u, organization: org } = useAuthStore.getState();
      if (isAuditRole(u?.role) && org?.id) return org.id;
      return null;
    });
  }, [getAccessTokenSilently, isAuthenticated]);

  // Initialize authStore
  useEffect(() => {
    const initializeAuthStore = async () => {
      if (isAuthenticated && !isInitialized) {
        await initialize();
        setIsInitialized(true);
      }
    };
    initializeAuthStore();
  }, [isAuthenticated, initialize, isInitialized]);

  // Activity tracking
  useEffect(() => {
    if (isAuthenticated && user) {
      const displayName = user.name || user.nickname || user.email || 'Utilisateur';
      setCurrentUserForActivities(displayName);
    }
  }, [isAuthenticated, user]);

  // Auth0 loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    loginWithRedirect();
    return null;
  }

  // Loading user context
  if (authStoreLoading || !isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement du contexte auditeur...</p>
        </div>
      </div>
    );
  }

  // If user is not an auditor, redirect to main app
  if (!isAuditRole(authStoreUser?.role)) {
    return <Navigate to="/" replace />;
  }

  return (
    <ThemeProvider>
      <AuditorLayout>
        <Outlet />
      </AuditorLayout>
    </ThemeProvider>
  );
}
